import { Appointment, SUBJECT_KEYS } from "@elixir/types";
import { Modal, notification } from "antd";
import useCancelAppointmentMutation from "../../../apis/tryst/cancel-appointment";
import useArchiveAppointment from "../../../apis/tryst/delete-archive-appointment";
import useMarkNoShowMutation from "../../../apis/tryst/post-mark-no-show";
import { ReactComponent as BanRegularIcon } from "../../../assets/icons/font-awesome/ban-regular.svg";
import { ReactComponent as CircleXmarkRegularIcon } from "../../../assets/icons/font-awesome/circle-xmark-regular.svg";
import { ReactComponent as TrashRegularIcon } from "../../../assets/icons/font-awesome/trash-regular.svg";
import { ReactComponent as XMarkRegularIcon } from "../../../assets/icons/font-awesome/xmark-regular.svg";
import { QUEUE_TYPE } from "../../../constants/appointments";
import { getPatientMainInfoString } from "../../../utils/appointment-utils";
import { track } from "../../../utils/tracking";
import { getAclViewClassName } from "@elixir/utils";

const DeleteAppointmentPopover = ({
  queueType,
  apt,
  onClose,
}: {
  queueType: QUEUE_TYPE;
  onClose: () => void;
  apt: Appointment;
}) => {
  const { patient, formData } = apt || {};
  const {
    mutateAsync: postMarkNoShowAppointment,
    isLoading: isMarkNoShowLoading,
  } = useMarkNoShowMutation();
  const {
    mutateAsync: putCancelAppointment,
    isLoading: isCancelAppointmentLoading,
  } = useCancelAppointmentMutation();
  const {
    mutateAsync: postArchiveAppointment,
    isLoading: isArchiveAppointmentLoading,
  } = useArchiveAppointment();

  return (
    <div className="pb-8" onClick={(e) => e.stopPropagation()}>
      <div className="BodyBody1Bold p-16 h-64 text-center flex items-center justify-between relative">
        <XMarkRegularIcon
          onClick={onClose}
          className="w-16 dr-min-w-16 dr-min-h-16 dr-ml-8 left-16 dr-text-darwin-touch-neutral-1000 dr-cursor-pointer"
        />
        <div className="px-3 dr-min-w-full">
          {getPatientMainInfoString({ patient, formData })?.join(" | ")}
        </div>
      </div>
      <div
        className={`dr-w-full dr-h-1 dr-mb-8 dr-bg-doctor-darwin-neutral-100 ${getAclViewClassName(SUBJECT_KEYS.MARK_NO_SHOW)}`}
      />
      <Item
        onClick={async () => {
          track("mark_no_show", {
            queueType,
          });
          const response = await postMarkNoShowAppointment({
            appointment: apt,
          });
          if (response.success) {
            notification.success({
              message: "Marked No Show",
              description: `Your appointment with ${apt.patient.fln} has been marked as no show.`,
              placement: "bottomRight",
            });
            onClose();
          } else {
            notification.error({
              message: "Error in marking no show",
              description: response.error,
              placement: "bottomRight",
            });
          }
        }}
        icon={
          <BanRegularIcon className="my-2 w-13 h-14 dr-max-w-13 dr-max-h-13 dr-text-doctor-darwin-neutral-900" />
        }
        label="Mark No Show"
        about="Patient did not turn up at the given time"
        className={`${getAclViewClassName(SUBJECT_KEYS.MARK_NO_SHOW)}`}
      />
      <div
        className={`dr-w-full dr-h-1 dr-mt-0 dr-mb-8 dr-bg-doctor-darwin-neutral-100 ${getAclViewClassName(SUBJECT_KEYS.CANCEL_APPT)}`}
      />
      <Item
        onClick={async () => {
          track("mark_cancelled", {
            queueType,
          });
          const response = await putCancelAppointment({ appointment: apt });
          if (response.success) {
            notification.success({
              message: "Appointment Cancelled",
              placement: "bottomRight",
              description: `Your appointment with ${apt.patient.fln} has been cancelled.`,
            });
            onClose();
          } else {
            notification.error({
              message: "Error in cancelling appointment",
              placement: "bottomRight",
              description: response.error,
            });
          }
        }}
        icon={
          <CircleXmarkRegularIcon className="my-2 w-13 h-14 dr-max-w-13 dr-max-h-13 dr-text-doctor-darwin-neutral-900" />
        }
        label="Mark Cancelled"
        about="Patient cancelled the appointment"
        className={`${getAclViewClassName(SUBJECT_KEYS.CANCEL_APPT)}`}
      />
      <div
        className={`dr-w-full dr-h-1 dr-mt-0 dr-mb-8 dr-bg-doctor-darwin-neutral-100 ${getAclViewClassName(
          SUBJECT_KEYS.DELETE_APPOINTMENT
        )}`}
      />
      <Item
        onClick={async () => {
          track("delete_init", {
            queueType,
          });
          Modal.confirm({
            title: "Are You Sure You Want to Delete the Appointment?",
            content:
              "Deleting the appointment will permanently hide associated receipts, lab test, and medicine orders.",
            onOk: async () => {
              track("delete", {
                queueType,
              });
              const response = await postArchiveAppointment({
                appointment: apt,
              });
              if (response.success) {
                notification.success({
                  message: "Appointment Deleted",
                  placement: "bottomRight",
                  description: `Your appointment with ${apt.patient.fln} has been deleted.`,
                });
                onClose();
              } else {
                notification.error({
                  message: "Error in deleting appointment",
                  placement: "bottomRight",
                  description: "",
                });
              }
            },
          });
        }}
        icon={
          <TrashRegularIcon className="my-2 w-13 h-14 dr-max-w-13 dr-max-h-13 dr-text-doctor-darwin-status-warning-500" />
        }
        className={`dr-text-doctor-darwin-status-warning-500 ${getAclViewClassName(
          SUBJECT_KEYS.DELETE_APPOINTMENT
        )}`}
        label="Delete"
        about="Remove this appointment from all queues"
      />
    </div>
  );
};

export default DeleteAppointmentPopover;

const Item = ({
  label,
  about,
  icon,
  className = "dr-text-darwin-touch-neutral-1000",
  onClick,
}: {
  icon: JSX.Element;
  label: string;
  about: string;
  className?: string;
  onClick: () => void;
}) => {
  return (
    <div
      onClick={onClick}
      className={`flex dr-space-x-16 py-8 px-24 ripple dr-cursor-pointer ${className}`}
    >
      {icon}
      <div className="dr-space-y-8">
        <div className="BodyBody2Bold">{label}</div>
        <div className="dr-text-doctor-darwin-neutral-700 BodyBody2Regular">
          {about}
        </div>
      </div>
    </div>
  );
};
